import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function choice(items) {
  return items[Math.floor(Math.random() * items.length)];
}

function polyValue(coeffs, x) {
  return coeffs.reduce((sum, coeff, degree) => sum + coeff * (x ** degree), 0);
}

function derivativeValue(coeffs, x) {
  let sum = 0;
  for (let degree = 1; degree < coeffs.length; degree += 1) {
    sum += degree * coeffs[degree] * (x ** (degree - 1));
  }
  return sum;
}

function weightedX0() {
  const outer = [-4, -3, 3, 4];
  const inner = [-2, -1, 0, 1, 2];
  return Math.random() < 0.75 ? choice(outer) : choice(inner);
}

function buildWindow(center, span = 10) {
  let start = Math.round(center) - span / 2;
  let end = start + span;

  if (start > 0) {
    end -= start;
    start = 0;
  } else if (end < 0) {
    start -= end;
    end = 0;
  }

  return [Math.round(start), Math.round(end)];
}

function buildRanges(x0, y0) {
  return {
    xRange: buildWindow(x0),
    yRange: buildWindow(y0),
  };
}

function powerLatex(power) {
  return power === 1 ? "x" : `x^${power}`;
}

function polyLatex(coeffs) {
  const parts = [];
  for (let degree = coeffs.length - 1; degree >= 0; degree -= 1) {
    const coeff = coeffs[degree];
    if (coeff === 0) continue;

    let term = "";
    if (degree === 0) {
      term = `${Math.abs(coeff)}`;
    } else if (degree === 1) {
      term = Math.abs(coeff) === 1 ? "x" : `${Math.abs(coeff)}x`;
    } else {
      term = Math.abs(coeff) === 1 ? powerLatex(degree) : `${Math.abs(coeff)}${powerLatex(degree)}`;
    }

    if (parts.length === 0) {
      parts.push(coeff < 0 ? `-${term}` : term);
    } else {
      parts.push(coeff < 0 ? `- ${term}` : `+ ${term}`);
    }
  }

  return parts.length ? parts.join(" ") : "0";
}

function derivativeLatex(coeffs) {
  const derived = [];
  for (let degree = 1; degree < coeffs.length; degree += 1) {
    derived[degree - 1] = degree * coeffs[degree];
  }
  return polyLatex(derived);
}

function lineLatex(m, d) {
  if (m === 0) return `${d}`;
  const mPart = m === 1 ? "x" : m === -1 ? "-x" : `${m}x`;
  if (d === 0) return mPart;
  return d > 0 ? `${mPart} + ${d}` : `${mPart} - ${Math.abs(d)}`;
}

function lineLatexFromUser(aParsed, aRaw, bParsed, bRaw) {
  if (!Number.isFinite(aParsed) || !Number.isFinite(bParsed)) return null;

  const aLatexRaw = String(formatLatexPreview(aRaw || ""));
  const bLatexRaw = String(formatLatexPreview(bRaw || ""));
  const aLatex = aLatexRaw.includes("=") ? aLatexRaw.split("=")[1].trim() : aLatexRaw;
  const bLatex = bLatexRaw.includes("=") ? bLatexRaw.split("=")[1].trim() : bLatexRaw;

  if (aParsed === 0) {
    return `y = ${bLatex}`;
  }

  const aPart = aParsed === 1 ? "x" : aParsed === -1 ? "-x" : `${aLatex}x`;
  if (bParsed === 0) return `y = ${aPart}`;
  return `y = ${aPart} ${bParsed >= 0 ? "+" : "-"} ${bLatex.replace("-", "")}`;
}

function polynomialExpr(coeffs) {
  const parts = [];
  for (let degree = coeffs.length - 1; degree >= 0; degree -= 1) {
    const coeff = coeffs[degree];
    if (coeff === 0) continue;
    if (degree === 0) {
      parts.push(coeff > 0 && parts.length ? `+${coeff}` : `${coeff}`);
    } else if (degree === 1) {
      parts.push(coeff > 0 && parts.length ? `+${coeff}x` : `${coeff}x`);
    } else {
      parts.push(coeff > 0 && parts.length ? `+${coeff}x^${degree}` : `${coeff}x^${degree}`);
    }
  }
  return `y=${parts.join("")}`;
}

function lineExpr(m, d) {
  if (m === 0) return `y=${d}`;
  const parts = [`${m}x`];
  if (d !== 0) parts.push(d > 0 ? `+${d}` : `${d}`);
  return `y=${parts.join("")}`;
}

function generateCoefficients() {
  for (let attempt = 0; attempt < 1000; attempt += 1) {
    const x0 = weightedX0();
    const y0 = randInt(-4, 4);
    const degree = choice([3, 4]);
    const leading = randInt(-1, 1, [0]);
    const slope = randInt(-5, 5, [0]);
    const c0 = randInt(-5, 5);
    const c1 = randInt(-4, 4);
    const c2 = randInt(-2, 2);

    let coeffs;
    if (degree === 3) {
      const a3 = leading;
      const a2 = c2;
      const a1 = slope - (3 * a3 * x0 * x0 + 2 * a2 * x0);
      const a0 = y0 - (a3 * x0 ** 3 + a2 * x0 ** 2 + a1 * x0);
      coeffs = [a0, a1, a2, a3];
    } else {
      const a4 = leading;
      const a3 = randInt(-1, 1);
      const a2 = c2;
      const a1 = slope - (4 * a4 * x0 ** 3 + 3 * a3 * x0 ** 2 + 2 * a2 * x0);
      const a0 = y0 - (a4 * x0 ** 4 + a3 * x0 ** 3 + a2 * x0 ** 2 + a1 * x0);
      coeffs = [a0, a1, a2, a3, a4];
    }

    const m = derivativeValue(coeffs, x0);
    const d = y0 - m * x0;
    const { xRange, yRange } = buildRanges(x0, y0);

    const coeffsReasonable = coeffs.every((coeff) => Math.abs(coeff) <= 18);
    const tangentReasonable = Math.abs(m) <= 5 && d >= yRange[0] - 2 && d <= yRange[1] + 2;
    const localVisible = [x0 - 1, x0, x0 + 1]
      .filter((x) => x >= xRange[0] && x <= xRange[1])
      .every((x) => {
        const y = polyValue(coeffs, x);
        return y >= yRange[0] && y <= yRange[1];
      });

    if (coeffsReasonable && tangentReasonable && localVisible) {
      return { coeffs, x0, y0, m, d, xRange, yRange };
    }
  }

  const coeffs = [1, -6, 1, 1];
  const x0 = 3;
  const y0 = polyValue(coeffs, x0);
  const m = derivativeValue(coeffs, x0);
  const d = y0 - m * x0;
  const { xRange, yRange } = buildRanges(x0, y0);
  return { coeffs, x0, y0, m, d, xRange, yRange };
}

export function generateTask() {
  const { coeffs, x0, y0, m, d, xRange, yRange } = generateCoefficients();

  return {
    type: "PolynomialTangentTask",
    prompt: `Wyznacz rownanie stycznej do wykresu funkcji f(x) = ${polyLatex(coeffs)} w punkcie o odcietej x_0 = ${x0}. Podaj wspolczynniki a i b prostej y = ax + b.`,
    answer: `a=${m}, b=${d}`,
    data: {
      coeffs,
      x0,
      y0,
      tangentA: m,
      tangentB: d,
      functionLatex: polyLatex(coeffs),
      derivativeLatex: derivativeLatex(coeffs),
      tangentLatex: lineLatex(m, d),
      xRange,
      yRange,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Wyznacz rownanie stycznej do wykresu funkcji:</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <p>w punkcie o odcietej <InlineMath math={`x_0 = ${data.x0}`} />.</p>
      <p>Podaj wspolczynniki <InlineMath math={"a"} /> i <InlineMath math={"b"} /> prostej <InlineMath math={"y=ax+b"} />.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer && typeof userAnswer === "object" ? userAnswer : {};
  return (
    <div className="space-y-3">
      <div>
        <div style={{ marginBottom: "0.35rem" }}><InlineMath math="a =" /></div>
        <FractionInput
          value={value.answerA || ""}
          onChange={(next) => onChange({ ...value, answerA: next })}
          disabled={disabled}
          label="a"
        />
      </div>
      <div>
        <div style={{ marginBottom: "0.35rem" }}><InlineMath math="b =" /></div>
        <FractionInput
          value={value.answerB || ""}
          onChange={(next) => onChange({ ...value, answerB: next })}
          disabled={disabled}
          label="b"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const aValue = userAnswer.answerA?.parsed;
  const bValue = userAnswer.answerB?.parsed;
  return typeof aValue === "number" && typeof bValue === "number"
    && Math.abs(aValue - data.tangentA) < 0.0001
    && Math.abs(bValue - data.tangentB) < 0.0001;
}

export function renderUserAnswer(_taskOrAnswer, maybeUserAnswer) {
  const userAnswer = maybeUserAnswer !== undefined ? maybeUserAnswer : _taskOrAnswer;
  if (!userAnswer || typeof userAnswer !== "object") return <i>(brak odpowiedzi)</i>;
  const rawA = userAnswer.answerA?.raw;
  const rawB = userAnswer.answerB?.raw;
  if (!rawA && !rawB) return <i>(brak odpowiedzi)</i>;

  const aValue = userAnswer.answerA?.parsed;
  const bValue = userAnswer.answerB?.parsed;
  const userLineLatex = lineLatexFromUser(aValue, rawA, bValue, rawB);

  return (
    <div className="space-y-2">
      {rawA ? <div><InlineMath math={formatLatexPreview(rawA, "a")} /></div> : <div><InlineMath math={"a = ?"} /></div>}
      {rawB ? <div><InlineMath math={formatLatexPreview(rawB, "b")} /></div> : <div><InlineMath math={"b = ?"} /></div>}
      {userLineLatex ? (
        <div style={{ marginTop: "0.25rem" }}>
          <InlineMath math={userLineLatex} />
        </div>
      ) : null}
    </div>
  );
}

export function renderAnswer({ data }) {
  return (
    <div className="space-y-1">
      <div><InlineMath math={`a = ${data.tangentA}`} /></div>
      <div><InlineMath math={`b = ${data.tangentB}`} /></div>
      <div><InlineMath math={`y = ${data.tangentLatex}`} /></div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const functionExpr = polynomialExpr(data.coeffs);
  const tangentExpr = lineExpr(data.tangentA, data.tangentB);

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p><strong>Krok 1:</strong> Liczymy pochodna funkcji.</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <BlockMath math={`f'(x) = ${data.derivativeLatex}`} />
      <p><strong>Krok 2:</strong> Obliczamy wspolczynnik kierunkowy stycznej.</p>
      <BlockMath math={`f'(${data.x0}) = ${data.tangentA}`} />
      <p><strong>Krok 3:</strong> Wyznaczamy punkt stycznosci.</p>
      <BlockMath math={`f(${data.x0}) = ${data.y0}`} />
      <BlockMath math={`P = (${data.x0}, ${data.y0})`} />
      <p><strong>Krok 4:</strong> Korzystamy ze wzoru prostej <InlineMath math={"y = ax + b"} />.</p>
      <BlockMath math={`${data.y0} = ${data.tangentA}\\cdot ${data.x0} + b`} />
      <BlockMath math={`b = ${data.tangentB}`} />
      <p><strong>Krok 5:</strong> Zapisujemy rownanie stycznej.</p>
      <BlockMath math={`y = ${data.tangentLatex}`} />
      <div style={{ maxWidth: 350, margin: "24px auto 0" }}>
        <GraphViewer
          width={350}
          xRange={data.xRange}
          yRange={data.yRange}
          equations={[
            { expr: functionExpr, color: "#ff8c1a" },
            { expr: tangentExpr, color: "#9d4edd" },
          ]}
          points={{
            P: { x: data.x0, y: data.y0, label: "P", color: "#cc5500", textColor: "#cc5500", textPosition: "top center" },
          }}
        />
      </div>
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
